import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { pdf } from '@react-pdf/renderer'
import React from 'react'
import { valueExistsTS } from '../../../../utilsTS'
import { formConditions } from '../editor/ConditionalElementEditor'

export function purgeInvalidPDFSignFromString (text: string): string {
  return valueExistsTS(text)
    ? String(text)
      .replaceAll('\t', ' ')
      .replaceAll(
        /[\u00A0\u1680​\u180e\u2000-\u2009\u200a​\u200b​\u200b\u202f\u205f​\u3000\u0009\uF000-\uFFFF]/g,
        ' '
      )
    : ''
}

interface ConditionConfigProps {
  condition: string
  conditionTarger: string
  parameter: string
  targetIndex: string
  elementType: string
  affectedElementType: string
  state:
  | 'show'
  | 'hide'
  | 'altLabel'
  | 'altHelpText'
  | 'required'
  | 'notRequired'
  | 'sub'
}

export function formConditionToText (conditionConfig: ConditionConfigProps): string {
  let toRet = ''
  const { state, condition, elementType, affectedElementType, parameter } =
    conditionConfig
  if (condition === null || condition === undefined) {
    return toRet
  }

  if (state === 'required' && affectedElementType === 'uploadFiles') {
    toRet = String(i18n._(t`PDF_REQUIRED_IF`)) + ' '
  } else if (state === 'show' || state === 'required') {
    toRet =
      affectedElementType === 'uploadFiles'
        ? String(i18n._(t`PDF_CONDITION_IF`)) + ' '
        : String(i18n._(t`PDF_CONDITION_ANSWER_IF`)) + ' '
  } else if (state === 'hide' || state === 'notRequired') {
    toRet = String(i18n._(t`PDF_CONDITION_DO_NOT_ANSWER_IF`)) + ' '
  } else if (state !== 'sub') {
    return toRet
  }
  const config = formConditions[elementType]?.conditions?.[condition]
  if (valueExistsTS(config?.manualFillText)) {
    toRet += String(i18n._(config.manualFillText))
    if (valueExistsTS(parameter)) {
      toRet += ` '${parameter}' `
    }
  }

  toRet +=
    String(i18n._(t`PDF_CONDITION_IN_QUESTION`)) + ` ${conditionConfig.targetIndex}.`

  return toRet
}

export const downloadOrphanedPDF = async (
  pdfDocument: React.ReactElement,
  fileName: string
): Promise<boolean> => {
  document.body.style.cursor = 'wait'
  return await pdf(pdfDocument)
    .toBlob()
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName + '.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      document.body.style.cursor = ''
      return true
    })
    .catch(reject => {
      console.error('pdf rejection', reject)
      document.body.style.cursor = ''
      return false
    })
}

export function formMinFilesConditionsToText (
  conditions: any,
  idToRenderIndex: Record<string, number> = {},
  elementsMap: Record<string, any> = {}
): string {
  if (!conditions) return ''

  const conditionsArray = Array.isArray(conditions)
    ? conditions
    : [conditions].filter(Boolean)

  const texts: string[] = []

  const resolveTargetIndex = (cond: any) => {
    if (idToRenderIndex[cond.conditionTarget] !== undefined) {
      return idToRenderIndex[cond.conditionTarget]
    }

    const elementEntry = Object.values(elementsMap).find(
      (el: any) =>
        el.sfField && `${el.sfObject}//${el.sfField}` === cond.conditionTarget
    )

    if (elementEntry && idToRenderIndex[elementEntry.id] !== undefined) {
      return idToRenderIndex[elementEntry.id]
    }

    return '?'
  }

  conditionsArray
    .filter(c => c.conditionTarget != null && c.state === 'minFiles' && c.sfField == null)
    .forEach(cond => {
      if (!['minFiles', 'sub'].includes(cond.state)) return

      const elementType =
      elementsMap[cond.conditionTarget]?.elementType || 'textInputNumeric'
      let text = `${i18n._(t`PDF_FILES_REQUIRED`)} `

      if (Array.isArray(cond.conditions) && cond.conditions.length > 0) {
        const subConditionsText = (() => {
          if (!Array.isArray(cond.conditions) || cond.conditions.length === 0) return ''

          const nonSalesforceSubs = cond.conditions
            .map(sub => {
              const isSubSalesforceField = sub.conditionTarget?.includes('//')
              if (isSubSalesforceField) return null

              const subElementType =
              elementsMap[sub.conditionTarget]?.elementType || 'textInputNumeric'
              const subConfig =
              formConditions[subElementType]?.conditions?.[sub.condition]
              const subText =
              subConfig?.manualFillText ||
              subConfig?.text ||
              sub.condition
              const paramPart = sub.parameter ? ` '${sub.parameter}'` : ''
              const subTargetIndex = resolveTargetIndex(sub)
              return `${i18n._(
              subText
            )}${paramPart} ${i18n._(t`PDF_IN_QUESTION`)} ${subTargetIndex}`
            })
            .filter(Boolean)

          if (nonSalesforceSubs.length > 1) {
            const restAreSalesforce = cond.conditions
              .slice(1)
              .every(sub => sub.conditionTarget?.includes('//'))

            if (restAreSalesforce) {
              return nonSalesforceSubs[0]
            }
          }

          if (nonSalesforceSubs.length > 1) {
            return nonSalesforceSubs.join(` ${i18n._(t`PDF_AND`)} `)
          }

          return nonSalesforceSubs[0] ?? ''
        })()

        text += subConditionsText
      } else {
        const config = formConditions[elementType]?.conditions?.[cond.condition]
        if (config) {
          text += String(
            i18n._(config.manualFillText || config.text || cond.condition)
          )
          if (cond.parameter) text += ` '${cond.parameter}'`
        } else if (cond.parameter) {
          text += `${cond.condition} '${cond.parameter}'`
        } else {
          text += cond.condition
        }
        text += ` ${i18n._(t`PDF_IN_QUESTION`)} ${resolveTargetIndex(cond)}`
      }

      texts.push(text)
    })

  return texts.join(' ')
}
